import React from 'react';

/**
 * Componente per renderizzare testo con sintassi Markdown in HTML formattato
 * Supporta: grassetto (**text**), liste (- item), tabelle (| col |), e nuove righe
 */
export default function MarkdownRenderer({ content }) {
    const renderContent = (text) => {
        if (!text) return null;

        // Divide il contenuto in righe
        const lines = text.split('\n');
        const elements = [];
        let currentTable = null;
        let currentList = null;
        let i = 0;

        while (i < lines.length) {
            const line = lines[i];
            const trimmedLine = line.trim();

            // Gestione tabelle
            if (trimmedLine.startsWith('|')) {
                if (!currentTable) {
                    currentTable = [];
                }
                currentTable.push(line);
                i++;
                continue;
            } else if (currentTable) {
                // Fine tabella
                elements.push(renderTable(currentTable, elements.length));
                currentTable = null;
            }

            // Gestione liste
            if (trimmedLine.startsWith('-') && trimmedLine.length > 1 && trimmedLine[1] === ' ') {
                if (!currentList) {
                    currentList = [];
                }
                currentList.push(trimmedLine.substring(2));
                i++;
                continue;
            } else if (currentList) {
                // Fine lista
                elements.push(renderList(currentList, elements.length));
                currentList = null;
            }

            // Righe normali (con supporto per grassetto)
            if (trimmedLine) {
                elements.push(
                    <p key={`p-${i}`} className="mb-3">
                        {renderInlineFormatting(line)}
                    </p>
                );
            } else {
                // Riga vuota
                elements.push(<div key={`space-${i}`} className="h-2" />);
            }

            i++;
        }

        // Chiudi eventuali liste o tabelle ancora aperte
        if (currentTable) {
            elements.push(renderTable(currentTable, elements.length));
        }
        if (currentList) {
            elements.push(renderList(currentList, elements.length));
        }

        return elements;
    };

    const renderInlineFormatting = (text) => {
        const parts = [];
        let currentText = text;
        let key = 0;

        // Gestione grassetto **text**
        const boldRegex = /\*\*(.+?)\*\*/g;
        let lastIndex = 0;
        let match;

        while ((match = boldRegex.exec(currentText)) !== null) {
            // Testo prima del grassetto
            if (match.index > lastIndex) {
                parts.push(currentText.substring(lastIndex, match.index));
            }
            // Testo in grassetto
            parts.push(
                <strong key={`bold-${key++}`} className="font-bold text-[--text-primary]">
                    {match[1]}
                </strong>
            );
            lastIndex = match.index + match[0].length;
        }

        // Testo rimanente
        if (lastIndex < currentText.length) {
            parts.push(currentText.substring(lastIndex));
        }

        return parts.length > 0 ? parts : currentText;
    };

    const renderList = (items, keyPrefix) => {
        return (
            <ul key={`list-${keyPrefix}`} className="mb-4 space-y-2 pl-5">
                {items.map((item, index) => (
                    <li key={`li-${index}`} className="flex items-start">
                        <span className="text-[--accent] mr-2">•</span>
                        <span className="flex-1">{renderInlineFormatting(item)}</span>
                    </li>
                ))}
            </ul>
        );
    };

    const renderTable = (tableLines, keyPrefix) => {
        // Filtra le righe di separazione (es: |---|---|)
        const contentLines = tableLines.filter(
            (line) => !line.trim().match(/^\|[\s\-:|]+\|$/)
        );

        if (contentLines.length === 0) return null;

        // Prima riga è l'header
        const headerLine = contentLines[0];
        const headers = headerLine
            .split('|')
            .map((h) => h.trim())
            .filter((h) => h);

        // Righe successive sono i dati
        const dataRows = contentLines.slice(1).map((line) =>
            line
                .split('|')
                .map((cell) => cell.trim())
                .filter((cell) => cell)
        );

        return (
            <div key={`table-${keyPrefix}`} className="mb-4 overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="border-b-2 border-[--accent]/30">
                            {headers.map((header, index) => (
                                <th
                                    key={`th-${index}`}
                                    className="px-4 py-2 text-left font-bold text-[--text-primary] bg-[--accent]/10"
                                >
                                    {renderInlineFormatting(header)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {dataRows.map((row, rowIndex) => (
                            <tr
                                key={`tr-${rowIndex}`}
                                className="border-b border-[--border-color] hover:bg-[--accent]/5 transition-colors"
                            >
                                {row.map((cell, cellIndex) => (
                                    <td key={`td-${cellIndex}`} className="px-4 py-2">
                                        {renderInlineFormatting(cell)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return <div className="markdown-content">{renderContent(content)}</div>;
}
